import React, { useState, useEffect } from 'react';
import { Tooltip, IconButton, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Helmet from 'react-helmet';

import {
    Portlet,
    PortletBody,
    PortletHeader,
} from "../../partials/content/Portlet";
import { PagedTable, BulkActionIconButton } from '../../common';
import EditIcon from '@mui/icons-material/Edit';
import { PayerImport } from './PayerImport';
import { EnableDialog } from './EnableDialog';
import { PayerDialog } from './PayerDialog';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import classNames from 'classnames';
import { ToleranceMatchDialog } from './ToleranceMatchDialog';
import { DependencyDialog } from './DependencyDialog';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { ROLE_ADMIN, ROLE_PARTNER_ADMIN, ROLE_SUPERADMIN } from '../../store/constants/roles';
import GetAppIcon from '@mui/icons-material/GetApp';

const DEFAULT_SORT = [{ id: 'name', direction: 'asc' }];
const SHOW_SEARCH_FILTERS = true;

const useStyles = makeStyles((theme) => ({
    root: {
        margin: 'auto',
    },
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
    },

    icon: {
        fontSize: 17,
        marginTop: 1,
    }
}));

export default function RemitPayersPage({
    intl,
    payerData, payerFields, payerContracts, userRole, clientName, selectedItems, remitPayerSelectAllPending,
    actions: {
        remitPayersLanding,
        searchGetByName,
        searchReset,
        resetPagedTable,
        setRemitPayerEditDialog,
        setList,
        setRemitPayerEnableDialog,
        payersGetPayerContractsTiny,
        setRemitPayToleranceMatchDialog,
        setRemitDependencyDialog,
        setSelectedItems,
        downloadRemitPayerReport,
        remitPayerSelectAll
    }
}) {
    const [isImportDialogOpen, setImportDialog] = useState(false);
    const [refresh, setRefresh] = useState(Promise.resolve());
    const [currentGridState, setCurrentGridState] = useState({ filter: null, sort: DEFAULT_SORT });

    const classes = useStyles();

    useEffect(() => {
        const pageName = {
            name: 'remit-payers-landing'
        }
        searchGetByName(pageName);

        return function cleanup() {
            searchReset();
            resetPagedTable(pageName.name);
            setSelectedItems([]);
        }
    }, [searchGetByName]);

    useEffect(() => {
        payersGetPayerContractsTiny()
    }, [payersGetPayerContractsTiny]);

    const handlePayerEdit = (payer) => {
        setRemitPayerEditDialog({ open: true, payer: payer, bulk: false });
    };

    // const handleCheckbox = (selectedRows, payer) => {
    //     updatePayerStatus(payer.id, !payer.active, handleRefresh);
    // };

    const handleAddPayer = () => {
        setRemitPayerEditDialog({ open: true, payer: null, bulk: false });
    }

    function handleLoad(sort, filter, pagination) {
        setCurrentGridState({ filter, sort });
        remitPayersLanding(pagination, sort, filter);
    }

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }

    function handleExportRemitPayers() {
        const payload = {
            page: null,
            filter: currentGridState.filter || null,
            sort: currentGridState.sort || [],
        };

        downloadRemitPayerReport(payload, downloadCallback);
    }

    function downloadCallback(blob) {
        const fileName = `${clientName}_RemitPayers.csv`;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    const handleClose = (shouldReloadGrid) => {
        setRemitPayerEditDialog(null);
        setRemitPayerEnableDialog(null);
        setRemitPayToleranceMatchDialog(null);
        setRemitDependencyDialog(null);
        if (shouldReloadGrid === true) {
            setList([]);
            handleRefresh();
        }
    }


    function handleImportPayerInfo() {
        setImportDialog(true);
    }

    function handleImportDialogClose() {
        setList([]);
        setImportDialog(false);
        handleRefresh();
    }

    function handlePayerConfirmation(isEnable) {
        setRemitPayerEnableDialog({
            open: true,
            isEnableClicked: isEnable
        })
    }

    function handleBulkDelete(processAll) {
        if (processAll) {
            // If API supports delete all by filter
            // For now, maybe just set a flag or select all in state
        }
        setRemitDependencyDialog({ open: true, processAll });
    }

    const handleBulkEnable = (processAll, selectedIds) => {
        const options = {
            ids: selectedIds,
            enabled: true,
            processAll,
            ...(processAll ? { filter: currentGridState.filter || {}, page: null, sort: currentGridState.sort || [] } : {})
        };
        remitPayerSelectAll(options, () => {
            setSelectedItems([]);
            handleRefresh();
        });
    }

    const handleBulkDisable = (processAll, selectedIds) => {
        const options = {
            ids: selectedIds,
            enabled: false,
            processAll,
            ...(processAll ? { filter: currentGridState.filter || {}, page: null, sort: currentGridState.sort || [] } : {})
        };
        remitPayerSelectAll(options, () => {
            setSelectedItems([]);
            handleRefresh();
        });
    }

    const handleBulkEditAction = (processAll) => {
        setRemitPayerEditDialog({ open: true, payer: null, bulk: true, processAll });
    }

    const handleBulkExportAction = (processAll) => {
        if (processAll) {
            handleExportRemitPayers(); // Already uses filter
        } else {
            // Need a way to export ONLY selected
            handleExportRemitPayers();
        }
    }

    function handleToleranceMatch() {
        setRemitPayToleranceMatchDialog({ open: true });
    }

    function tableLevelActions() {
        return (
            <>
                <Grid item>
                    <Tooltip title="Export">
                        <IconButton
                            aria-label="Export"
                            size="small"
                            onClick={() => handleBulkExportAction(false)}
                        >
                            <GetAppIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                {(userRole === ROLE_SUPERADMIN || userRole === ROLE_PARTNER_ADMIN || userRole === ROLE_ADMIN) &&
                    <Grid item>
                        <Tooltip title="Delete Remit Payers">
                            <IconButton
                                aria-label="Delete"
                                size="small"
                                onClick={() => handleBulkDelete(false)}
                                disabled={selectedItems.length === 0}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                }
                <Grid item>
                    <Tooltip title="Import Remit Payers Count">
                        <IconButton aria-label="download" size='small'
                            onClick={handleImportPayerInfo}
                        >
                            <i className="fas fa-upload"></i>
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <Tooltip title="Edit">
                        <IconButton
                            aria-label="Edit"
                            size="small"
                            onClick={() => handleBulkEditAction(false)}
                            disabled={selectedItems.length === 0}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <BulkActionIconButton
                        pageName="remit-payers-landing"
                        actionLabel="Enable"
                        onConfirm={handleBulkEnable}
                        icon={<CheckCircleIcon />}
                        size="small"
                        tooltip="Enable"
                        isPending={remitPayerSelectAllPending}
                        color="default"
                    />
                </Grid>
                <Grid item>
                    <BulkActionIconButton
                        pageName="remit-payers-landing"
                        actionLabel="Disable"
                        onConfirm={handleBulkDisable}
                        icon={<i className={classNames("fa fa-solid fa-ban", classes.icon)}></i>}
                        size="small"
                        tooltip="Disable"
                        isPending={remitPayerSelectAllPending}
                        color="default"
                    />
                </Grid>
                <Grid item>
                    <Tooltip title="Payer Matching">
                        <IconButton aria-label="edit" size='small' onClick={() => handleToleranceMatch()}>
                            <i className={classNames("fas fa-equals", classes.icon)}></i>
                        </IconButton>
                    </Tooltip>
                </Grid>
            </>
        );
    }

    function payerInfoActions(rowData) {
        return (
            <div className='d-flex'>
                <IconButton
                    color="primary"
                    aria-label="Edit"
                    onClick={() => handlePayerEdit(rowData)}
                    size="large">
                    <Tooltip title="Edit">
                        <EditIcon />
                    </Tooltip>
                </IconButton>
            </div>
        );
    }

    return (
        <>
            <Helmet title="Remit Payers" />
            <Portlet>
                <PortletHeader
                    title="Remit Payers"
                    handleRefresh={handleRefresh}
                    showSearchFilters={SHOW_SEARCH_FILTERS}
                    showSearchFiltersDropdown={false}
                    name="remit-payers-landing"
                />
                <PortletBody>
                    <div className="row">
                        <div className="col-md-12">
                            <PagedTable
                                name="remit-payers-landing"
                                fields={payerFields}
                                data={payerData}
                                onLoad={handleLoad}
                                refresh={refresh}
                                defaultSort={DEFAULT_SORT}
                                onAdd={handleAddPayer}
                                tableLevelActions={tableLevelActions}
                                hasCheckbox={true}
                                showSelectAll={true}
                                title="Remit Payer"
                                criteriasSaveEnabled={true}
                                showSearchFilters={SHOW_SEARCH_FILTERS}
                                hasActions={true}
                                shouldRenderLazy={true}
                                renderActionColumn={payerInfoActions}
                            />
                        </div>
                    </div>
                </PortletBody>
            </Portlet>
            <PayerImport open={isImportDialogOpen} handleClose={handleImportDialogClose} />
            <PayerDialog
                payerContracts={payerContracts}
                handleClose={handleClose}
            />
            <ToleranceMatchDialog
                handleClose={handleClose}
            />
            <DependencyDialog handleClose={handleClose} />
        </>
    );
}